/* Billing Review → shipment detail: charge breakdown (excl. GST), flags, ad hoc charges, history and POD links. */
import React from 'react'
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Table, TableBody,
  TableCell, TableRow, Typography } from '@mui/material'
import { useLineDetailQuery } from '../../../redux/ptlBillingApiSlice'
import { ADHOC_STATE, errText, fmtDate, fmtDateTime, inr, pct } from '../billingUtils'

const HEADS = [
  ['freight', 'Freight (after minimum)'], ['fuelSurcharge', 'Fuel surcharge'], ['cod', 'COD'], ['docket', 'Docket'],
  ['appointment', 'Appointment'], ['oda', 'ODA'], ['specialZone', 'Special zone'], ['greenTax', 'Green tax'], ['fov', 'FOV'],
  ['handling', 'Handling'], ['rounding', 'Rounding (Proship pricing)'], ['unexplained', 'Charge with no breakup entry'],
]

export const STATUS_CHIP = {
  PENDING: { label: 'PENDING', color: 'default' },
  APPROVED: { label: 'APPROVED', color: 'success' },
  REOPENED: { label: 'RE-OPENED', color: 'warning' },
  BILLED: { label: 'BILLED', color: 'info' },
}

export default function LineDetailDialog({ id, onClose }) {
  const { data, isFetching, error } = useLineDetailQuery(id)
  const l = data?.line
  const s = data?.shipment || {}
  // reasons that stop approval vs. warnings that are only shown for review (an older server sends no
  // blockingMessages: then a blocked line keeps every flag under "Blocked", as before)
  const blocking = l?.blockingMessages || (l?.blocked ? l.flagMessages || [] : [])
  const review = (l?.flagMessages || []).filter((m) => !blocking.includes(m))
  return (
    <Dialog open onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>
        {l ? `${l.awb} · ${l.merchantName || ''}` : 'Shipment'}
        {l && (
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
            {l.courierName} · {s.pickupCity || '?'} {s.pickupPincode || ''} → {s.dropCity || '?'} {s.dropPincode || ''} {s.dropState ? `(${s.dropState})` : ''}
            {' '}· lane {l.lane || '—'} · picked {fmtDate(l.pickupAt)} · {l.outcome === 'LOST' ? 'lost' : 'delivered'} {fmtDate(l.outcomeAt)}
            {' '}· billing {l.cycleMonth}{l.rolledOverFrom ? ` (rolled over from ${l.rolledOverFrom})` : ''}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {isFetching && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={28} /></Box>}
        {error && <Alert severity='error'>{errText(error)}</Alert>}
        {l && !isFetching && (
          <Box sx={{ display: 'grid', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Chip size='small' color={STATUS_CHIP[l.status]?.color} label={STATUS_CHIP[l.status]?.label || l.status} />
              {l.outcome !== 'DELIVERED' && <Chip size='small' color='error' variant='outlined' label={l.outcome} />}
              {l.atMinFreight && <Chip size='small' variant='outlined' label='MINIMUM FREIGHT' />}
              {l.reopenNote && <Typography sx={{ fontSize: 13 }}>Re-open note: <i>{l.reopenNote}</i></Typography>}
            </Box>
            {blocking.length > 0 && (
              <Alert severity='error'>
                Blocked: cannot be approved until fixed.
                <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>{blocking.map((m) => <li key={m}>{m}</li>)}</ul>
              </Alert>
            )}
            {review.length > 0 && (
              <Alert severity='warning'>
                {blocking.length > 0 ? 'Also review (does not block approval):' : 'Review (does not block approval):'}
                <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>{review.map((m) => <li key={m}>{m}</li>)}</ul>
              </Alert>
            )}

            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { md: '1fr 1fr' } }}>
              <Box>
                <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: '.04em', color: 'text.secondary', mb: 0.5 }}>CHARGES (FROM THE PRICE PROSHIP STORED, EXCL. GST)</Typography>
                <Table size='small'>
                  <TableBody>
                    <TableRow><TableCell sx={{ fontSize: 13 }}>Chargeable weight</TableCell><TableCell align='right'>{l.chargeableWeightKg ?? '—'} kg</TableCell></TableRow>
                    {HEADS.filter(([k]) => Number(l[k] || 0) !== 0 || k === 'freight' || k === 'fuelSurcharge').map(([k, label]) => (
                      <TableRow key={k}><TableCell sx={{ fontSize: 13 }}>{label}</TableCell><TableCell align='right'>{inr(l[k])}</TableCell></TableRow>
                    ))}
                    <TableRow><TableCell sx={{ fontSize: 13, fontWeight: 600 }}>Base (Proship price {inr(l.systemPriceBeforeTax)})</TableCell><TableCell align='right' sx={{ fontWeight: 600 }}>{inr(l.baseTotal)}</TableCell></TableRow>
                  </TableBody>
                </Table>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: '.04em', color: 'text.secondary', mb: 0.5 }}>ADDED BY BILLING</Typography>
                <Table size='small'>
                  <TableBody>
                    <TableRow><TableCell sx={{ fontSize: 13 }}>DPH (diesel ₹{l.dieselPrice ?? '—'})</TableCell><TableCell align='right'>{inr(l.dhp)}</TableCell></TableRow>
                    {(l.surcharges || []).map((x) => (
                      <TableRow key={x.code}><TableCell sx={{ fontSize: 13 }}>{x.label}</TableCell><TableCell align='right'>{inr(x.amount)}</TableCell></TableRow>
                    ))}
                    {!(l.surcharges || []).length && <TableRow><TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>Surcharges</TableCell><TableCell align='right'>{inr(0)}</TableCell></TableRow>}
                    {Number(l.legacyAdhocTotal) !== 0 && <TableRow><TableCell sx={{ fontSize: 13 }}>Additional charges (old upload)</TableCell><TableCell align='right'>{inr(l.legacyAdhocTotal)}</TableCell></TableRow>}
                    <TableRow><TableCell sx={{ fontSize: 13 }}>Ad hoc (client side)</TableCell><TableCell align='right'>{inr(l.adhocClientTotal)}</TableCell></TableRow>
                    <TableRow><TableCell sx={{ fontSize: 13, fontWeight: 700 }}>Total (excl. GST)</TableCell><TableCell align='right' sx={{ fontWeight: 700 }}>{inr(l.totalExclGst)}</TableCell></TableRow>
                    <TableRow><TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>Est. courier cost (incl. ad hoc {inr(l.adhocCourierTotal)})</TableCell><TableCell align='right'>{inr(l.estCourierCost)}</TableCell></TableRow>
                    <TableRow><TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>Margin</TableCell>
                      <TableCell align='right' sx={{ color: l.margin < 0 ? 'error.main' : 'success.dark' }}>{inr(l.margin)} ({pct(l.marginPct)})</TableCell></TableRow>
                  </TableBody>
                </Table>
              </Box>
            </Box>

            {data.adhoc.length > 0 && (
              <Box>
                <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: '.04em', color: 'text.secondary', mb: 0.5 }}>AD HOC CHARGES</Typography>
                {data.adhoc.map((a) => (
                  <Typography key={a.id} sx={{ fontSize: 13 }}>
                    <Chip size='small' color={ADHOC_STATE[a.state]?.color} label={ADHOC_STATE[a.state]?.label} sx={{ mr: 1 }} />
                    {a.chargeTypeLabel}{a.otherDesc ? ` (${a.otherDesc})` : ''}: client {a.clientAmount != null ? inr(a.clientAmount) : 'waiting for KAM'}
                    {' '}/ courier {a.courierAmount != null ? inr(a.courierAmount) : 'waiting for Ops'}{a.remarks ? ` · ${a.remarks}` : ''}
                  </Typography>
                ))}
              </Box>
            )}

            <Box>
              <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: '.04em', color: 'text.secondary', mb: 0.5 }}>POD</Typography>
              {data.podLinks.length === 0 ? <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{l.outcome === 'LOST' ? 'Not needed (lost).' : 'No POD on the shipment.'}</Typography>
                : data.podLinks.filter((u) => /^https?:\/\//i.test(u)).map((u, i) => <Button key={u} size='small' href={u} target='_blank' rel='noopener noreferrer' sx={{ textTransform: 'none' }}>Open POD {data.podLinks.length > 1 ? i + 1 : ''}</Button>)}
            </Box>

            <Divider />
            <Box>
              <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: '.04em', color: 'text.secondary', mb: 1 }}>HISTORY OF THIS SHIPMENT</Typography>
              {data.history.map((h, i) => (
                <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: 1, mb: 0.75, fontSize: 13 }}>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{fmtDateTime(h.at)}</Typography>
                  <Typography sx={{ fontSize: 13 }}>
                    <Chip size='small' label={h.action.replace('_', ' ')} sx={{ mr: 1 }} /><b>{h.by}</b>
                    {h.note ? ` · ${h.note}` : ''}{h.totalExclGst != null ? ` · total ${inr(h.totalExclGst)}` : ''}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  )
}
